/**
 * TrainingProgressContext — the in-memory progress store for the Study screens.
 *
 * This is the seam the TR-5 server-backed store slots into: the screens read and
 * mutate progress ONLY through this hook, never a global. Today it lives in React
 * state (session-scoped, resets on reload) so a new user gets honest empty states;
 * TR-5 replaces the provider's internals with the event-sourced, per-(user,company)
 * store and the screens don't change. Mirrors the design's `state.*` progress
 * shape and its accuracy/coverage helpers.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { questions, type TestKey, type TrainingQuestion } from '@/content/training';
import { AuthContext } from '@/contexts/AuthContext';
import {
  apiFetchProgress,
  apiFetchExamHistory,
  apiFinishExam,
  apiPostEvents,
  newEventId,
  type FinishedExam,
  type TrainingEvent,
} from './trainingApi';
import { TrainingOutbox, outboxKey, type FinishAnswer } from './progressOutbox';
import { replayEvents } from './progressReplay';

function browserStorage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}

export interface QuestionStat {
  seen: number;
  right: number;
  wrong: number;
}

export interface LastLesson {
  id: string;
  /** section index within the lesson */
  section: number;
}

export interface ExamAttempt {
  /** The server attempt, when the exam was started on the server. */
  id?: string;
  test: TestKey;
  score: number;
  total: number;
  passed: boolean;
  pct: number;
  when: number;
}

interface ProgressState {
  lessonsDone: Record<string, boolean>;
  qStats: Record<string, QuestionStat>;
  missed: Record<string, boolean>;
  cardKnown: Record<string, boolean>;
  bitesRead: Record<string, boolean>;
  lastLesson: LastLesson | null;
  examHistory: ExamAttempt[];
}

const EMPTY: ProgressState = {
  lessonsDone: {},
  qStats: {},
  missed: {},
  cardKnown: {},
  bitesRead: {},
  lastLesson: null,
  examHistory: [],
};

export interface TrainingProgress extends ProgressState {
  markLessonDone: (lessonId: string) => void;
  setLastLesson: (lessonId: string, section: number) => void;
  /** Record an answer to a question: updates stats and the missed set. */
  recordAnswer: (question: TrainingQuestion, chosenIndex: number) => void;
  setCardKnown: (cardId: string, known: boolean) => void;
  markBiteRead: (biteId: string) => void;
  clearMissed: (questionId: string) => void;
  /** Records a finished exam attempt; the finish time is stamped here. */
  addExamAttempt: (attempt: Omit<ExamAttempt, 'when'>) => void;
  /** Sends a server-started exam's answers for grading; kept and sent again until the server has it. */
  submitExamFinish: (attemptId: string, answers: FinishAnswer[]) => void;
  /** % correct across answered questions for a test, or null if none answered. */
  accuracyFor: (test: TestKey) => number | null;
  /** % of the test's question bank that has been seen at least once. */
  coverageFor: (test: TestKey) => number;
  /** The currently-missed questions, in bank order. */
  missedList: () => TrainingQuestion[];
  /**
   * Why progress is NOT being saved, when the cause is something the user can fix.
   * `no_active_company` = they belong to several companies with no default set, so
   * the server can't tell which company their progress belongs to. Null otherwise
   * (saving fine, or a transient/unknown failure we stay quiet about).
   */
  saveBlocked: 'no_active_company' | null;
}

const Ctx = createContext<TrainingProgress | null>(null);

export function TrainingProgressProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ProgressState>(EMPTY);
  const [saveBlocked, setSaveBlocked] = useState<TrainingProgress['saveBlocked']>(null);
  // Whether the persistence API answered on load. Until it does, and whenever it
  // is unavailable, everything stays in-memory — identical to the pre-TR-5 UX, so
  // nothing breaks before the PROD migration is applied. A ref (not state) so
  // emitting an event never triggers a re-render.
  const persistedRef = useRef(false);
  // Hydration is async; a user can act before it resolves (e.g. Bite Size marks
  // the first bite read on mount). Until it settles we BUFFER emitted events
  // rather than drop them, so the initial server snapshot can't erase a mutation
  // the user already made. On a successful hydrate we merge (local wins) and
  // flush the buffer; on failure we discard it (in-memory mode has nothing to
  // persist).
  const settledRef = useRef(false);
  const pendingRef = useRef<TrainingEvent[]>([]);
  // Sends saves one batch at a time and keeps what didn't reach the server
  // (progressOutbox.ts). Kept per signed-in person, so another account on this
  // browser never sends it.
  const outboxRef = useRef<TrainingOutbox | null>(null);
  const userId = useContext(AuthContext)?.user?.id ?? null;
  const userIdRef = useRef(userId);
  userIdRef.current = userId;
  // The person the save queue was made for. If someone else signs in while this stays
  // mounted, the queue stops: its retries (a 401 is retried) never go out under their
  // login. The work stays in the browser under its owner's key for their next visit.
  const outboxOwnerRef = useRef<string | null>(null);
  useEffect(() => {
    if (outboxRef.current && outboxOwnerRef.current !== userId) outboxRef.current.stop();
  }, [userId]);
  // latest state for event-handler guards (avoids reading stale closures)
  const stateRef = useRef(state);
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  // Hydrate from the server once; a failure leaves the empty in-memory state.
  useEffect(() => {
    let alive = true;
    const uid = userIdRef.current;
    const outbox = new TrainingOutbox({
      postEvents: apiPostEvents,
      finishExam: apiFinishExam,
      storage: uid ? browserStorage() : null,
      storageKey: uid ? outboxKey(uid) : null,
      onFinished: (exam: FinishedExam) => {
        if (!alive) return;
        // An attempt the server just graded shows once: it may already be in the
        // history (finished this visit, or read from the server).
        setState((s) =>
          s.examHistory.some((a) => a.id === exam.id)
            ? s
            : {
                ...s,
                examHistory: [
                  ...s.examHistory,
                  {
                    id: exam.id,
                    test: exam.test_key,
                    score: exam.score,
                    total: exam.total,
                    passed: exam.passed,
                    pct: exam.pct,
                    when: exam.finished_at ? Date.parse(exam.finished_at) : Date.now(),
                  },
                ],
              },
        );
      },
    });
    outboxRef.current = outbox;
    outboxOwnerRef.current = uid;
    (async () => {
      try {
        // Saves an earlier visit couldn't send go first, so the reads below include them.
        if (outbox.hasBacklog()) await outbox.flush();
        if (!alive) return;
        // Settle the two reads INDEPENDENTLY: persistence availability hinges on
        // /progress (that's the row we read + write). A transient /exams failure
        // must not drop us to in-memory for the whole mount and strand every later
        // write — history just falls back to empty in that case.
        const [progRes, histRes] = await Promise.allSettled([
          apiFetchProgress(),
          apiFetchExamHistory(),
        ]);
        if (!alive) return;
        if (progRes.status !== 'fulfilled') throw progRes.reason;
        const { progress } = progRes.value;
        const companyId = progRes.value.company_id ?? null;
        // A failed history read keeps what is already shown (an attempt the backlog just
        // sent is there), rather than replacing it with nothing (#1200 review).
        const attempts = histRes.status === 'fulfilled' ? histRes.value.attempts ?? [] : null;
        persistedRef.current = true;
        settledRef.current = true;
        // Events from now on are kept with the company this progress was read for.
        outbox.setCompany(companyId);
        // Progress kept from an earlier visit that still couldn't be sent (for this same
        // company) is replayed over the server's copy, so it shows now rather than after the
        // next successful send and reload (#1200 review).
        const server = replayEvents(
          {
            lessonsDone: progress?.lessons_done ?? {},
            bitesRead: progress?.bites_read ?? {},
            cardKnown: progress?.cards_known ?? {},
            qStats: progress?.q_stats ?? {},
            missed: progress?.missed ?? {},
            lastLesson: progress?.last_lesson ?? null,
          },
          outbox.pendingEventsFor(companyId),
        );
        // Merge, don't clobber: spread the server snapshot first, then the
        // current in-memory state, so any mutation the user made while hydration
        // was in flight (buffered below) wins over the older server value. The
        // buffered events are flushed to the ledger right after, so the server
        // reconciles too. History is server-authoritative.
        setState((s) => ({
          ...s,
          lessonsDone: { ...server.lessonsDone, ...s.lessonsDone },
          bitesRead: { ...server.bitesRead, ...s.bitesRead },
          cardKnown: { ...server.cardKnown, ...s.cardKnown },
          qStats: { ...server.qStats, ...s.qStats },
          missed: { ...server.missed, ...s.missed },
          lastLesson: s.lastLesson ?? server.lastLesson,
          // GET /exams returns newest-first; the in-memory history is oldest-first
          // (push order) and the UI reverses it to show newest-first, so store the
          // server attempts oldest-first too or the list renders upside-down.
          examHistory:
            attempts === null
              ? s.examHistory
              : attempts
                  .map((a) => ({
                    id: a.id,
                    test: a.test_key,
                    score: a.score,
                    total: a.total,
                    passed: a.passed,
                    pct: a.pct,
                    when: a.finished_at ? Date.parse(a.finished_at) : Date.now(),
                  }))
                  .reverse(),
        }));
        // send anything the user did during hydration
        const pending = pendingRef.current;
        pendingRef.current = [];
        outbox.addEvents(pending);
      } catch (err) {
        // persistence unavailable — stay in-memory (pre-TR-5 behaviour); the
        // buffered events have nowhere to go, so drop them.
        settledRef.current = true;
        pendingRef.current = [];
        // The one failure the user can fix themselves: tell them, instead of
        // silently not saving (it used to surface only as console 400s).
        if (alive && (err as { code?: unknown } | null)?.code === 'no_active_company') {
          setSaveBlocked('no_active_company');
        }
      }
    })();
    return () => {
      alive = false;
      outbox.stop();
      if (outboxRef.current === outbox) outboxRef.current = null;
    };
  }, []);

  // Event emit; never throws into the UI. Before hydration settles we buffer (so a
  // mutation isn't lost to the initial snapshot); after, the save queue sends it if
  // persistence is available (in order, sent again if it fails), else it is dropped
  // (in-memory mode).
  const emit = useCallback((events: TrainingEvent[]) => {
    if (!settledRef.current) {
      pendingRef.current.push(...events);
      return;
    }
    if (!persistedRef.current) return;
    outboxRef.current?.addEvents(events);
  }, []);

  const submitExamFinish = useCallback((attemptId: string, answers: FinishAnswer[]) => {
    const outbox = outboxRef.current;
    if (outbox) outbox.addFinish(attemptId, answers);
    else apiFinishExam(attemptId, answers).catch(() => {});
  }, []);

  const markLessonDone = useCallback(
    (lessonId: string) => {
      setState((s) => ({ ...s, lessonsDone: { ...s.lessonsDone, [lessonId]: true } }));
      emit([{ event_id: newEventId(), type: 'lesson_done', id: lessonId }]);
    },
    [emit],
  );

  const setLastLesson = useCallback(
    (lessonId: string, section: number) => {
      setState((s) => ({ ...s, lastLesson: { id: lessonId, section } }));
      emit([{ event_id: newEventId(), type: 'last_lesson', id: lessonId, value: section }]);
    },
    [emit],
  );

  const recordAnswer = useCallback(
    (question: TrainingQuestion, chosenIndex: number) => {
      const right = chosenIndex === question.answer;
      setState((s) => {
        const prev = s.qStats[question.id] ?? { seen: 0, right: 0, wrong: 0 };
        const qStats = {
          ...s.qStats,
          [question.id]: {
            seen: prev.seen + 1,
            right: prev.right + (right ? 1 : 0),
            wrong: prev.wrong + (right ? 0 : 1),
          },
        };
        const missed = { ...s.missed };
        if (right) delete missed[question.id];
        else missed[question.id] = true;
        return { ...s, qStats, missed };
      });
      emit([
        {
          event_id: newEventId(),
          type: right ? 'question_right' : 'question_wrong',
          id: question.id,
        },
      ]);
    },
    [emit],
  );

  const setCardKnown = useCallback(
    (cardId: string, known: boolean) => {
      setState((s) => {
        const cardKnown = { ...s.cardKnown };
        if (known) cardKnown[cardId] = true;
        else delete cardKnown[cardId];
        return { ...s, cardKnown };
      });
      emit([{ event_id: newEventId(), type: known ? 'card_known' : 'card_unknown', id: cardId }]);
    },
    [emit],
  );

  const markBiteRead = useCallback(
    (biteId: string) => {
      if (stateRef.current.bitesRead[biteId]) return; // already read — no churn, no re-emit
      setState((s) => (s.bitesRead[biteId] ? s : { ...s, bitesRead: { ...s.bitesRead, [biteId]: true } }));
      emit([{ event_id: newEventId(), type: 'bite_read', id: biteId }]);
    },
    [emit],
  );

  const clearMissed = useCallback((questionId: string) => {
    setState((s) => {
      if (!s.missed[questionId]) return s;
      const missed = { ...s.missed };
      delete missed[questionId];
      return { ...s, missed };
    });
  }, []);

  const addExamAttempt = useCallback((attempt: Omit<ExamAttempt, 'when'>) => {
    setState((s) => ({
      ...s,
      examHistory: [...s.examHistory, { ...attempt, when: Date.now() }],
    }));
  }, []);

  const accuracyFor = useCallback(
    (test: TestKey) => {
      let right = 0;
      let wrong = 0;
      for (const q of questions) {
        if (q.test !== test) continue;
        const stat = state.qStats[q.id];
        if (stat) {
          right += stat.right;
          wrong += stat.wrong;
        }
      }
      const n = right + wrong;
      return n === 0 ? null : Math.round((100 * right) / n);
    },
    [state.qStats],
  );

  const coverageFor = useCallback(
    (test: TestKey) => {
      let seen = 0;
      let total = 0;
      for (const q of questions) {
        if (q.test !== test) continue;
        total++;
        if (state.qStats[q.id]) seen++;
      }
      return total === 0 ? 0 : Math.round((100 * seen) / total);
    },
    [state.qStats],
  );

  const missedList = useCallback(
    () => questions.filter((q) => state.missed[q.id]),
    [state.missed],
  );

  const value = useMemo<TrainingProgress>(
    () => ({
      ...state,
      markLessonDone,
      setLastLesson,
      recordAnswer,
      setCardKnown,
      markBiteRead,
      clearMissed,
      addExamAttempt,
      submitExamFinish,
      accuracyFor,
      coverageFor,
      missedList,
      saveBlocked,
    }),
    [
      state,
      saveBlocked,
      markLessonDone,
      setLastLesson,
      recordAnswer,
      setCardKnown,
      markBiteRead,
      clearMissed,
      addExamAttempt,
      submitExamFinish,
      accuracyFor,
      coverageFor,
      missedList,
    ],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useTrainingProgress(): TrainingProgress {
  const ctx = useContext(Ctx);
  if (!ctx) {
    throw new Error('useTrainingProgress must be used within a TrainingProgressProvider');
  }
  return ctx;
}
